import React, { useCallback, useEffect, useState } from 'react';
import { Cross2Icon, Pencil1Icon, PersonIcon } from '@radix-ui/react-icons';
import { Button, Spinner } from '../../components';
import { Lead, LeadStatus } from '../../types/lead';
import { leadService } from '../../services/leadService';
import { useToastStore } from '../../store/toastStore';
import { formatApiError } from '../../utils/errorHandler';
import { formatDateTime, formatIsoDateForDisplay } from '../../utils/formatters';
import './leadFormShared.scss';
import './LeadViewPanel.scss';

const STATUS_LABELS: Record<LeadStatus, string> = {
  new_lead: 'New Lead',
  lead: 'Lead',
  progress: 'In Progress',
  qualified: 'Qualified',
  pending: 'Pending',
  converted: 'Converted',
};

const STATUS_PILL_CLASS: Record<LeadStatus, string> = {
  new_lead: 'lead-view-modal__pill--lead',
  lead: 'lead-view-modal__pill--lead',
  progress: 'lead-view-modal__pill--progress',
  qualified: 'lead-view-modal__pill--qualified',
  pending: 'lead-view-modal__pill--progress',
  converted: 'lead-view-modal__pill--converted',
};

const displayValue = (value: string | null | undefined): string =>
  value?.trim() ? value.trim() : '—';

const DetailField: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div>
    <span>{label}</span>
    {typeof value === 'string' ? <strong>{value}</strong> : value}
  </div>
);

const CUSTOMER_FIELDS: {
  label: string;
  getValue: (lead: Lead) => React.ReactNode;
}[] = [
  { label: 'Date', getValue: (lead) => displayValue(formatIsoDateForDisplay(lead.leadDate)) },
  { label: 'Source', getValue: (lead) => displayValue(lead.leadSource?.name) },
  { label: 'Counsellor', getValue: (lead) => displayValue(lead.counsellorName) },
  { label: 'Name', getValue: (lead) => displayValue(lead.fullName) },
  { label: 'Email', getValue: (lead) => displayValue(lead.email) },
  { label: 'Contact Number', getValue: (lead) => displayValue(lead.phoneNumber) },
  { label: 'Gender', getValue: (lead) => displayValue(lead.gender) },
  { label: 'Age', getValue: (lead) => displayValue(lead.age) },
  { label: 'Qualification', getValue: (lead) => displayValue(lead.qualification) },
  { label: 'Occupation', getValue: (lead) => displayValue(lead.occupation) },
  { label: 'Caste', getValue: (lead) => displayValue(lead.caste) },
  { label: 'Height', getValue: (lead) => displayValue(lead.height) },
  { label: 'Marital Status', getValue: (lead) => displayValue(lead.maritalStatus) },
  { label: 'If Divorce', getValue: (lead) => displayValue(lead.divorceInfo) },
  { label: 'Location', getValue: (lead) => displayValue(lead.location) },
  { label: 'Requirements', getValue: (lead) => displayValue(lead.requirements) },
  {
    label: 'Looking For',
    getValue: (lead) => displayValue(lead.preferredGender),
  },
];

const FOLLOW_UP_FIELDS: { label: string; key: keyof Lead }[] = [
  { label: 'Remarks 1st Time (Date)', key: 'remarks1stTime' },
  { label: '2nd call date', key: 'secondCallDate' },
  { label: 'On 3rd call (Date)', key: 'thirdCallDate' },
  { label: 'Bio-data Received or Not (Date)', key: 'biodataReceived' },
  { label: 'Profile Created or Not (Date)', key: 'profileCreated' },
  { label: 'Membership (Free|Paid) Date', key: 'membership' },
  { label: 'Amount and How many profile to be share (Date)', key: 'amountProfilesShared' },
  { label: 'Shared (Date)', key: 'shared' },
  { label: 'Left', key: 'left' },
  { label: 'Retainer form sent or not (date)', key: 'retainerFormSent' },
  { label: 'Retainer Signed or not by client (date)', key: 'retainerSigned' },
  { label: 'Any other Remark (date)', key: 'anyOtherRemark' },
];

export interface LeadViewPanelProps {
  isOpen: boolean;
  leadId?: string;
  onClose: () => void;
  onEdit?: (leadId: string) => void;
  onFollowUp?: (leadId: string) => void;
}

export const LeadViewPanel: React.FC<LeadViewPanelProps> = ({
  isOpen,
  leadId,
  onClose,
  onEdit,
  onFollowUp,
}) => {
  const showErrorMessage = useToastStore((state) => state.showErrorMessage);
  const [lead, setLead] = useState<Lead | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !leadId) {
      setLead(null);
      setLoadError(null);
      return;
    }

    let cancelled = false;

    (async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const data = await leadService.getLeadById(leadId);
        if (!cancelled) {
          setLead(data);
        }
      } catch (err) {
        if (!cancelled) {
          const message = formatApiError(err).trim() || 'Unable to load lead.';
          setLoadError(message);
          showErrorMessage({
            title: 'Failed to load lead',
            message,
          });
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, leadId, showErrorMessage]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleEdit = useCallback(() => {
    if (!leadId || !onEdit) return;
    onEdit(leadId);
  }, [leadId, onEdit]);

  const handleFollowUp = useCallback(() => {
    if (!leadId || !onFollowUp) return;
    onFollowUp(leadId);
  }, [leadId, onFollowUp]);

  if (!isOpen) return null;

  return (
    <div className="lead-view-modal" role="presentation">
      <div
        className="lead-view-modal__overlay"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className="lead-view-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-view-modal-title"
      >
        <header className="lead-view-modal__header">
          <div>
            <h2 id="lead-view-modal-title" className="lead-view-modal__title">
              Lead Details
            </h2>
            <p className="lead-view-modal__subtitle">
              View customer profile, follow-up remarks, and pipeline status.
            </p>
          </div>
          <button
            type="button"
            className="lead-view-modal__close"
            onClick={onClose}
            aria-label="Close dialog"
          >
            <Cross2Icon />
          </button>
        </header>

        {loadError && <div className="lead-view-modal__error">{loadError}</div>}

        {isLoading ? (
          <div className="lead-view-modal__loading">
            <Spinner size="medium" />
          </div>
        ) : lead ? (
          <>
            <div className="lead-view-modal__body">
              <section className="lead-form-shared__section lead-view-modal__section">
                <h3 className="lead-form-shared__section-title">Customer details</h3>
                <div className="lead-form-shared__meta-grid">
                  <DetailField
                    label="Lead ID"
                    value={displayValue(lead.leadUniqueId ?? lead.uniqueId)}
                  />
                  <DetailField
                    label="Status"
                    value={
                      <span
                        className={`lead-view-modal__pill ${STATUS_PILL_CLASS[lead.status]}`}
                      >
                        {STATUS_LABELS[lead.status]}
                      </span>
                    }
                  />
                  {CUSTOMER_FIELDS.map(({ label, getValue }) => (
                    <DetailField key={label} label={label} value={getValue(lead)} />
                  ))}
                </div>
              </section>

              <section className="lead-form-shared__section lead-view-modal__section">
                <h3 className="lead-form-shared__section-title">Follow-up &amp; remarks</h3>
                <div className="lead-form-shared__meta-grid">
                  {FOLLOW_UP_FIELDS.map(({ label, key }) => (
                    <DetailField
                      key={key}
                      label={label}
                      value={displayValue(lead[key] as string | null | undefined)}
                    />
                  ))}
                </div>
              </section>

              {lead.notes?.trim() ? (
                <section className="lead-form-shared__section lead-view-modal__section">
                  <h3 className="lead-form-shared__section-title">Notes</h3>
                  <div className="lead-view-modal__notes">
                    <p>{lead.notes.trim()}</p>
                  </div>
                </section>
              ) : null}

              <section className="lead-form-shared__section lead-view-modal__section">
                <h3 className="lead-form-shared__section-title">Record info</h3>
                <div className="lead-form-shared__meta-grid">
                  <DetailField label="Created" value={formatDateTime(lead.createdAt)} />
                  <DetailField label="Last Updated" value={formatDateTime(lead.modifiedAt)} />
                </div>
              </section>
            </div>

            <footer className="lead-view-modal__footer">
              <button
                type="button"
                className="lead-view-modal__cancel"
                onClick={onClose}
              >
                Close
              </button>
              {onEdit && (
                <Button onClick={handleEdit} className="lead-view-modal__edit">
                  <Pencil1Icon />
                  Edit Lead
                </Button>
              )}
              {onFollowUp && (
                <Button onClick={handleFollowUp} className="lead-view-modal__edit">
                  <Pencil1Icon />
                  Follow Up
                </Button>
              )}
            </footer>
          </>
        ) : !loadError ? (
          <div className="lead-view-modal__empty">
            <PersonIcon aria-hidden="true" />
            <p>Lead not found.</p>
          </div>
        ) : null}
      </div>
    </div>
  );
};
